import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../../redux/store";

const API_BASE = process.env.REACT_APP_API_URL || "";

interface StatEntry {
  value: number | null;
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface TransactionStatusCountEntry {
  status: number;
  label: string;
  count: number;
}

export type DashboardUserActivityPeriod =
  | "12_months"
  | "30_days"
  | "7_days"
  | "24_hours";

interface UserActivityPoint {
  name: string;
  value: number;
}

interface UserActivityEntry {
  data: UserActivityPoint[];
  loading: boolean;
  error: string | null;
  lastUpdated: string | null;
}

interface DashboardStatsState {
  dataupBalance: StatEntry;
  seagmBalance: StatEntry;
  totalUsers: StatEntry;
  transactionStatusCounts: TransactionStatusCountEntry[];
  userActivity: Record<DashboardUserActivityPeriod, UserActivityEntry>;
  transactionActivity: Record<DashboardUserActivityPeriod, UserActivityEntry>;
}

interface DashboardSummaryPayload {
  dataupBalance: number | null;
  seagmBalance: number | null;
  totalUsers: number | null;
  transactionStatusCounts: TransactionStatusCountEntry[];
}

const makeInitialStat = (): StatEntry => ({
  value: null,
  loading: false,
  error: null,
  lastUpdated: null,
});

const makeInitialUserActivity = (): UserActivityEntry => ({
  data: [],
  loading: false,
  error: null,
  lastUpdated: null,
});

const initialState: DashboardStatsState = {
  dataupBalance: makeInitialStat(),
  seagmBalance: makeInitialStat(),
  totalUsers: makeInitialStat(),
  transactionStatusCounts: [],
  userActivity: {
    "12_months": makeInitialUserActivity(),
    "30_days": makeInitialUserActivity(),
    "7_days": makeInitialUserActivity(),
    "24_hours": makeInitialUserActivity(),
  },
  transactionActivity: {
    "12_months": makeInitialUserActivity(),
    "30_days": makeInitialUserActivity(),
    "7_days": makeInitialUserActivity(),
    "24_hours": makeInitialUserActivity(),
  },
};

const extractNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
};

const resolveNumeric = (payload: unknown, fallbackText: string, candidateKeys: string[]): number | null => {
  const direct = extractNumeric(payload);
  if (direct !== null) return direct;

  if (payload && typeof payload === "object") {
    for (const key of candidateKeys) {
      const candidate = (payload as Record<string, unknown>)[key];
      const resolved = extractNumeric(candidate);
      if (resolved !== null) return resolved;
    }
    const dataField = (payload as Record<string, unknown>)["data"];
    if (dataField && typeof dataField === "object") {
      return resolveNumeric(dataField, fallbackText, candidateKeys);
    }
  }

  return extractNumeric(fallbackText);
};

const parseResponse = async (response: Response) => {
  const textPayload = await response.text();
  let parsedPayload: unknown = textPayload;
  try {
    parsedPayload = textPayload ? JSON.parse(textPayload) : null;
  } catch {
    parsedPayload = textPayload;
  }
  return { parsedPayload, textPayload };
};

const resolveTransactionStatusCounts = (
  payload: unknown,
): TransactionStatusCountEntry[] => {
  if (!payload || typeof payload !== "object") return [];

  const directCounts = (payload as Record<string, unknown>)[
    "transaction_status_counts"
  ];

  if (Array.isArray(directCounts)) {
    return directCounts
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;

        const record = entry as Record<string, unknown>;
        const status = extractNumeric(record.status);
        const count = extractNumeric(record.count);
        const label =
          typeof record.label === "string" ? record.label.trim() : "";

        if (status === null || count === null || !label) return null;

        return {
          status,
          label,
          count,
        };
      })
      .filter((entry): entry is TransactionStatusCountEntry => entry !== null);
  }

  const dataField = (payload as Record<string, unknown>)["data"];
  if (dataField && typeof dataField === "object") {
    return resolveTransactionStatusCounts(dataField);
  }

  return [];
};

const resolveUserActivityData = (payload: unknown): UserActivityPoint[] => {
  if (Array.isArray(payload)) {
    return payload
      .map((entry) => {
        if (!entry || typeof entry !== "object") return null;
        const record = entry as Record<string, unknown>;
        const value = extractNumeric(record.value);
        const name = typeof record.name === "string" ? record.name.trim() : "";

        if (value === null || !name) return null;

        return { name, value };
      })
      .filter((entry): entry is UserActivityPoint => entry !== null);
  }

  if (payload && typeof payload === "object") {
    const directData = (payload as Record<string, unknown>).data;
    return resolveUserActivityData(directData);
  }

  return [];
};

export const fetchDashboardSummary = createAsyncThunk<
  DashboardSummaryPayload,
  void,
  { rejectValue: string; state: RootState }
>("dashboardStats/fetchSummary", async (_, { getState, rejectWithValue }) => {
  const state = getState();
  const token = state.auth?.token;
  if (!token) return rejectWithValue("Unauthorized");

  try {
    const response = await fetch(`${API_BASE}/dashboard/summary`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const { parsedPayload, textPayload } = await parseResponse(response);

    if (!response.ok) {
      throw new Error("Failed to fetch dashboard summary");
    }

    return {
      dataupBalance: resolveNumeric(parsedPayload, textPayload, [
        "dataup_balance",
        "wallet_balance",
      ]),
      seagmBalance: resolveNumeric(parsedPayload, textPayload, [
        "seagm_balance",
        "balance",
        "amount",
        "value",
      ]),
      totalUsers: resolveNumeric(parsedPayload, textPayload, [
        "users_count",
        "total_users",
        "total",
        "count",
        "value",
      ]),
      transactionStatusCounts: resolveTransactionStatusCounts(parsedPayload),
    };
  } catch (err: any) {
    return rejectWithValue(err?.message ?? "Failed to fetch dashboard summary");
  }
});

export const fetchDashboardUserActivity = createAsyncThunk<
  { period: DashboardUserActivityPeriod; data: UserActivityPoint[] },
  DashboardUserActivityPeriod,
  { rejectValue: string; state: RootState }
>(
  "dashboardStats/fetchUserActivity",
  async (period, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const query = new URLSearchParams({ period }).toString();
      const response = await fetch(`${API_BASE}/dashboard/user-activity?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const { parsedPayload } = await parseResponse(response);

      if (!response.ok) {
        throw new Error("Failed to fetch user activity");
      }

      return {
        period,
        data: resolveUserActivityData(parsedPayload),
      };
    } catch (err: any) {
      return rejectWithValue(err?.message ?? "Failed to fetch user activity");
    }
  },
);

export const fetchDashboardTransactionActivity = createAsyncThunk<
  { period: DashboardUserActivityPeriod; data: UserActivityPoint[] },
  DashboardUserActivityPeriod,
  { rejectValue: string; state: RootState }
>(
  "dashboardStats/fetchTransactionActivity",
  async (period, { getState, rejectWithValue }) => {
    const state = getState();
    const token = state.auth?.token;
    if (!token) return rejectWithValue("Unauthorized");

    try {
      const query = new URLSearchParams({ period }).toString();
      const response = await fetch(
        `${API_BASE}/dashboard/transaction-activity?${query}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const { parsedPayload } = await parseResponse(response);

      if (!response.ok) {
        throw new Error("Failed to fetch transaction activity");
      }

      return {
        period,
        data: resolveUserActivityData(parsedPayload),
      };
    } catch (err: any) {
      return rejectWithValue(
        err?.message ?? "Failed to fetch transaction activity",
      );
    }
  },
);

const dashboardStatSlice = createSlice({
  name: "dashboardStats",
  initialState,
  reducers: {
    setDataupBalance(state, action: PayloadAction<number | null>) {
      state.dataupBalance.value = action.payload;
      state.dataupBalance.lastUpdated = new Date().toISOString();
      state.dataupBalance.error = null;
    },
    clearDataupBalance(state) {
      state.dataupBalance = makeInitialStat();
    },
    setSeagmBalance(state, action: PayloadAction<number | null>) {
      state.seagmBalance.value = action.payload;
      state.seagmBalance.lastUpdated = new Date().toISOString();
      state.seagmBalance.error = null;
    },
    clearSeagmBalance(state) {
      state.seagmBalance = makeInitialStat();
    },
    setTotalUsers(state, action: PayloadAction<number | null>) {
      state.totalUsers.value = action.payload;
      state.totalUsers.lastUpdated = new Date().toISOString();
      state.totalUsers.error = null;
    },
    clearTotalUsers(state) {
      state.totalUsers = makeInitialStat();
    },
    setTransactionStatusCounts(
      state,
      action: PayloadAction<TransactionStatusCountEntry[]>,
    ) {
      state.transactionStatusCounts = action.payload;
    },
    clearTransactionStatusCounts(state) {
      state.transactionStatusCounts = [];
    },
    clearDashboardUserActivity(
      state,
      action: PayloadAction<DashboardUserActivityPeriod | undefined>,
    ) {
      if (action.payload) {
        state.userActivity[action.payload] = makeInitialUserActivity();
        return;
      }

      state.userActivity = {
        "12_months": makeInitialUserActivity(),
        "30_days": makeInitialUserActivity(),
        "7_days": makeInitialUserActivity(),
        "24_hours": makeInitialUserActivity(),
      };
      state.transactionActivity = {
        "12_months": makeInitialUserActivity(),
        "30_days": makeInitialUserActivity(),
        "7_days": makeInitialUserActivity(),
        "24_hours": makeInitialUserActivity(),
      };
    },
    resetDashboardStats(state) {
      state.dataupBalance = makeInitialStat();
      state.seagmBalance = makeInitialStat();
      state.totalUsers = makeInitialStat();
      state.transactionStatusCounts = [];
      state.userActivity = {
        "12_months": makeInitialUserActivity(),
        "30_days": makeInitialUserActivity(),
        "7_days": makeInitialUserActivity(),
        "24_hours": makeInitialUserActivity(),
      };
      state.transactionActivity = {
        "12_months": makeInitialUserActivity(),
        "30_days": makeInitialUserActivity(),
        "7_days": makeInitialUserActivity(),
        "24_hours": makeInitialUserActivity(),
      };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.dataupBalance.loading = true;
        state.seagmBalance.loading = true;
        state.totalUsers.loading = true;
        state.dataupBalance.error = null;
        state.seagmBalance.error = null;
        state.totalUsers.error = null;
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.dataupBalance.loading = false;
        state.seagmBalance.loading = false;
        state.totalUsers.loading = false;
        state.dataupBalance.value = action.payload.dataupBalance;
        state.seagmBalance.value = action.payload.seagmBalance;
        state.totalUsers.value = action.payload.totalUsers;
        state.transactionStatusCounts = action.payload.transactionStatusCounts;
        state.dataupBalance.lastUpdated = new Date().toISOString();
        state.seagmBalance.lastUpdated = new Date().toISOString();
        state.totalUsers.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        const error = action.payload ?? "Failed to fetch dashboard summary";
        state.dataupBalance.loading = false;
        state.seagmBalance.loading = false;
        state.totalUsers.loading = false;
        state.dataupBalance.error = error;
        state.seagmBalance.error = error;
        state.totalUsers.error = error;
      })
      .addCase(fetchDashboardUserActivity.pending, (state, action) => {
        state.userActivity[action.meta.arg].loading = true;
        state.userActivity[action.meta.arg].error = null;
      })
      .addCase(fetchDashboardUserActivity.fulfilled, (state, action) => {
        state.userActivity[action.payload.period].loading = false;
        state.userActivity[action.payload.period].data = action.payload.data;
        state.userActivity[action.payload.period].lastUpdated =
          new Date().toISOString();
      })
      .addCase(fetchDashboardUserActivity.rejected, (state, action) => {
        const period = action.meta.arg;
        state.userActivity[period].loading = false;
        state.userActivity[period].error =
          action.payload ?? "Failed to fetch user activity";
      })
      .addCase(fetchDashboardTransactionActivity.pending, (state, action) => {
        state.transactionActivity[action.meta.arg].loading = true;
        state.transactionActivity[action.meta.arg].error = null;
      })
      .addCase(
        fetchDashboardTransactionActivity.fulfilled,
        (state, action) => {
          state.transactionActivity[action.payload.period].loading = false;
          state.transactionActivity[action.payload.period].data =
            action.payload.data;
          state.transactionActivity[action.payload.period].lastUpdated =
            new Date().toISOString();
        },
      )
      .addCase(fetchDashboardTransactionActivity.rejected, (state, action) => {
        const period = action.meta.arg;
        state.transactionActivity[period].loading = false;
        state.transactionActivity[period].error =
          action.payload ?? "Failed to fetch transaction activity";
      });
  },
});

export const {
  setDataupBalance,
  clearDataupBalance,
  setSeagmBalance,
  clearSeagmBalance,
  setTotalUsers,
  clearTotalUsers,
  setTransactionStatusCounts,
  clearTransactionStatusCounts,
  clearDashboardUserActivity,
  resetDashboardStats,
} = dashboardStatSlice.actions;

export const selectDashboardDataupBalance = (state: RootState) => state.dashboardStats.dataupBalance.value;
export const selectDashboardDataupBalanceLoading = (state: RootState) => state.dashboardStats.dataupBalance.loading;
export const selectDashboardDataupBalanceError = (state: RootState) => state.dashboardStats.dataupBalance.error;
export const selectDashboardDataupBalanceLastUpdated = (state: RootState) =>
  state.dashboardStats.dataupBalance.lastUpdated;

export const selectDashboardSeagmBalance = (state: RootState) => state.dashboardStats.seagmBalance.value;
export const selectDashboardSeagmBalanceLoading = (state: RootState) => state.dashboardStats.seagmBalance.loading;
export const selectDashboardSeagmBalanceError = (state: RootState) => state.dashboardStats.seagmBalance.error;
export const selectDashboardSeagmBalanceLastUpdated = (state: RootState) =>
  state.dashboardStats.seagmBalance.lastUpdated;

export const selectDashboardTotalUsers = (state: RootState) => state.dashboardStats.totalUsers.value;
export const selectDashboardTotalUsersLoading = (state: RootState) => state.dashboardStats.totalUsers.loading;
export const selectDashboardTotalUsersError = (state: RootState) => state.dashboardStats.totalUsers.error;
export const selectDashboardTotalUsersLastUpdated = (state: RootState) =>
  state.dashboardStats.totalUsers.lastUpdated;
export const selectDashboardTransactionStatusCounts = (state: RootState) =>
  state.dashboardStats.transactionStatusCounts;
export const selectDashboardUserActivity = (
  period: DashboardUserActivityPeriod,
) => (state: RootState) => state.dashboardStats.userActivity[period];
export const selectDashboardTransactionActivity = (
  period: DashboardUserActivityPeriod,
) => (state: RootState) => state.dashboardStats.transactionActivity[period];

export default dashboardStatSlice.reducer;
